// Investor-facing page: /investors
//
// Deliberately NOT wrapped in <PublicLayout/>: that layout's nav carries the
// full public site menu (Discover Rentals, Academy, Pricing, About, FAQ...),
// which is the opposite of what this page needs. The brief calls for a
// small, isolated nav (Product / Market Test / Business Model / Vision +
// Talk to Jeanjen) that can evolve on its own as traction appears, so this
// page owns its own minimal header and footer instead of adapting the
// site-wide ones. Nothing here touches PublicLayout, so no other page is
// affected.
//
// Content rules (see the implementation brief this page was built from):
//   - No invented traction: no customers, revenue, retention, testimonials,
//     partnerships, or investor logos.
//   - Product status: "already working, finishing the remaining parts
//     before launch", never "finished" or "fully launched".
//   - Corporation: "being prepared", never "incorporated".
//   - No em dashes anywhere in this page's copy.
//   - The hero/product visual is a REAL production component
//     (CustomerBookingDetail) fed realistic-but-fabricated demo data, the
//     same "sandboxed preloaded" pattern already used by the homepage
//     walkthroughs, not a mockup image, not invented UI.
import { useMemo } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowDown, Mail, CalendarCheck2, Car, Users, ClipboardList } from "lucide-react";
import SEO from "@/components/SEO";
import { BrandLogo } from "@/components/BrandLogo";
import CustomerBookingDetail from "@/pages/CustomerBookingDetail";
import { useOperatorContacts } from "@/lib/operatorContacts";

const CONTACT_EMAIL = "jeanjen.confirmph@gmail.com";
const FOUNDER_NAME = "Jeanjen Luberio";
const PRODUCT_TOUR_PATH = "/demo-business/scenarios";

// -----------------------------------------------------------------------
// Small, isolated nav: anchor-scrolls within this page only. Structured as
// data (not hardcoded markup) so it stays easy to extend later.
// -----------------------------------------------------------------------
const NAV_LINKS = [
  { href: "#product", label: "Product" },
  { href: "#market-test", label: "Market Test" },
  { href: "#business-model", label: "Business Model" },
  { href: "#vision", label: "Vision" },
];

// Section 5 status strip. Kept as data on purpose: this is the piece of the
// page most likely to change first (see the comment on StatusStrip below).
const STATUS_STRIP_ITEMS = [
  "Working product",
  "Philippines-first",
  "Commercial validation next",
];

// -----------------------------------------------------------------------
// Hero product visual: the real CustomerBookingDetail page (the same one
// renters use), fed a self-contained, hard-coded "preloaded" object so
// nothing here calls the backend. This mirrors the exact pattern already
// documented on CustomerBookingDetail itself ("optional sandboxed-demo
// hook... used by the homepage walkthrough") and reuses the same demo
// business/vehicle already used elsewhere on the site (Dumaguete Island
// Wheels, Toyota Vios · WSJ-2211: see promoter/demos/workflow/scripts.js)
// so this doesn't introduce a new, inconsistent fictional business.
//
// Three confirmation_versions are included on purpose: the confirmation
// history is the literal, on-screen proof behind the hero headline: a real
// booking that has genuinely been confirmed more than once.
function useInvestorBookingPreloaded() {
  return useMemo(() => {
    const now = new Date();
    const start = new Date(now.getTime() + 9 * 24 * 3600 * 1000);
    start.setHours(9, 0, 0, 0);
    const end = new Date(start.getTime() + 2 * 24 * 3600 * 1000);
    end.setHours(18, 0, 0, 0);
    const startIso = start.toISOString();
    const endIso = end.toISOString();
    const createdIso = new Date(now.getTime() - 3 * 24 * 3600 * 1000).toISOString();
    const editIso = new Date(now.getTime() - 2 * 24 * 3600 * 1000).toISOString();
    const reassignIso = new Date(now.getTime() - 1 * 24 * 3600 * 1000).toISOString();

    return {
      booking: {
        id: "investor-demo-booking",
        booking_reference: "DIW-2214",
        status: "confirmed",
        start_datetime: startIso,
        end_datetime: endIso,
        price: 3400,
        deposit_required: 1000,
        customer_name: "Maria Santos",
        customer_email: "maria@example.com",
        pickup_location: "Dumaguete Airport",
        return_location: "Dumaguete Airport",
      },
      company: {
        id: "investor-demo-company",
        name: "Dumaguete Island Wheels",
        city: "Dumaguete City",
        email: "hello@dumaguete-island-wheels.example",
        phone: "+63 917 555 0100",
      },
      vehicle: { id: "vios", name: "Toyota Vios", plate_number: "WSJ-2211" },
      confirmation_versions: [
        { id: "v1", version_number: 1, trigger: "booking_created", created_by_type: "staff", created_at: createdIso, sent_at: createdIso, confirmed_at: createdIso, confirmed_by_type: "customer" },
        { id: "v2", version_number: 2, trigger: "staff_edit", created_by_type: "staff", created_at: editIso, sent_at: editIso, confirmed_at: editIso, confirmed_by_type: "customer" },
        { id: "v3", version_number: 3, trigger: "vehicle_reassigned", created_by_type: "staff", created_at: reassignIso, sent_at: reassignIso, confirmed_at: reassignIso, confirmed_by_type: "customer" },
      ],
      deposit: { status: "pending", required: 1000, collected: 0 },
      review_status: { invited: false },
    };
  }, []);
}

export default function Investors() {
  return (
    <div className="min-h-screen bg-[#FAFAFA] text-slate-900" data-testid="investors-page">
      <SEO
        title="ConfirmPH for Investors | Rental Operations Platform"
        description="Learn what ConfirmPH is building for rental businesses in the Philippines, how we plan to enter the market, what we intend to prove, and where the company is headed next."
        canonical="/investors"
      />
      <InvestorHeader />
      <main>
        <Hero />
        <FounderIncidentSection />
        <ProblemSection />
        <ConnectSection />
        <DifferentSection />
        <ThesisSection />
        <StatusStrip />
        <StageSection />
        <MarketTestSection />
        <ProveSection />
        <BusinessModelSection />
        <WhyRentalsSection />
        <CompoundSection />
        <OpportunitySection />
        <FounderSection />
        <FinalCtaSection />
      </main>
      <InvestorFooter />
      <MobileStickyCta />
    </div>
  );
}

// -----------------------------------------------------------------------
// Header: logo + 4 anchor links + one CTA. No hamburger: on a single-page
// scroller with only four short section links, a drawer would add
// complexity without adding clarity, so the anchor links simply hide below
// lg: and the page relies on normal scrolling plus the mobile sticky CTA.
// -----------------------------------------------------------------------
function InvestorHeader() {
  return (
    <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/85 border-b border-slate-200" data-testid="investors-header">
      <div className="max-w-[1180px] mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-3">
        <Link to="/" data-testid="investors-logo-link" className="flex items-center gap-2 min-w-0">
          <BrandLogo variant="full" className="h-9 w-auto" alt="ConfirmPH" testId="investors-brand-logo" />
        </Link>
        <nav className="hidden lg:flex items-center gap-1 text-sm" aria-label="Investor page sections">
          {NAV_LINKS.map((l) => (
            <a key={l.href} href={l.href} className="px-3 py-2 rounded-md text-slate-700 hover:bg-slate-100">
              {l.label}
            </a>
          ))}
        </nav>
        <a
          href="#contact"
          data-testid="investors-header-talk-cta"
          className="inline-flex items-center px-4 py-2 rounded-lg btn-brand font-medium text-sm whitespace-nowrap"
        >
          Talk to Jeanjen
        </a>
      </div>
    </header>
  );
}

// -----------------------------------------------------------------------
// 4. HERO
// -----------------------------------------------------------------------
function Hero() {
  const preloaded = useInvestorBookingPreloaded();
  return (
    <section className="border-b border-slate-200 bg-white" data-testid="investors-hero">
      <div className="max-w-[1180px] mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
        <div className="motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-bottom-2 motion-safe:duration-700">
          <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold" data-testid="investors-hero-eyebrow">
            ConfirmPH for Investors
          </div>
          <h1 className="mt-4 font-display font-bold leading-[1.08] text-[2.5rem] sm:text-[3.25rem] lg:text-[3.5rem]">
            A customer reconfirms the booking three times.
            <br className="hidden sm:block" /> At pickup, they still hear:{" "}
            <br className="hidden sm:block" />
            <span className="text-slate-500">&ldquo;Sorry, it&rsquo;s not available.&rdquo;</span>
          </h1>
          <p className="mt-6 text-[17px] sm:text-[18px] leading-relaxed text-slate-600 max-w-[560px]">
            ConfirmPH connects bookings, vehicles, customers, staff and the operational work behind
            each rental, so fewer things depend on chats, spreadsheets and memory.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Link
              to={PRODUCT_TOUR_PATH}
              data-testid="investors-hero-cta-product"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-lg btn-brand font-semibold text-sm sm:text-base"
            >
              View the Product <ArrowRight className="w-4 h-4" />
            </Link>
            <a
              href="#contact"
              data-testid="investors-hero-cta-talk"
              className="inline-flex items-center px-5 py-3 rounded-lg border border-slate-300 bg-white hover:bg-slate-50 font-semibold text-sm sm:text-base"
            >
              Talk to Jeanjen
            </a>
          </div>
        </div>

        <div>
          <ProductVisual preloaded={preloaded} />
          <p className="mt-3 text-xs text-slate-500 text-center lg:text-left">
            Current ConfirmPH renter interface, showing a customer-confirmed booking and its confirmation history.
          </p>
        </div>
      </div>
    </section>
  );
}

function ThesisSection() {
  return (
    <section className="bg-[#10241B] text-white" data-testid="investors-thesis">
      <div className="max-w-[900px] mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10 text-center">
        <div className="text-xs uppercase tracking-[0.2em] text-emerald-200 font-semibold">Company thesis</div>
        <p className="mt-3 font-display text-2xl sm:text-3xl font-semibold leading-snug">
          ConfirmPH is building the operating and reputation layer for fragmented rental businesses,
          starting with vehicle rentals in the Philippines.
        </p>
      </div>
    </section>
  );
}

function FounderIncidentSection() {
  return (
    <section className="bg-[#F4F4F5]" data-testid="investors-founder-incident">
      <div className="max-w-[1180px] mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        <div className="grid lg:grid-cols-[0.72fr_1.28fr] gap-8 lg:gap-14 items-start">
          <div className="lg:sticky lg:top-24">
            <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">
              Why this problem became personal
            </div>
            <h2 className="mt-3 font-display text-[1.9rem] sm:text-[2.5rem] leading-tight font-bold">
              The opening story happened to me.
            </h2>
            <p className="mt-5 text-sm text-slate-500">Jeanjen Luberio, Founder</p>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-6 sm:p-8 shadow-sm">
            <div className="space-y-4 text-[17px] text-slate-700 leading-relaxed">
              <p>Before ConfirmPH, I had a rental booking that we reconfirmed three separate times.</p>
              <p>On the day of pickup, we arrived expecting the vehicle to be there.</p>
              <p>It wasn&rsquo;t.</p>
              <p>
                We were told the car had been involved in an accident <strong>three days earlier</strong>,
                but nobody had informed us.
              </p>
              <p>
                After several calls, the rental business eventually found a replacement vehicle.
              </p>
            </div>

            <div className="my-7 border-l-4 border-[#1B4332] pl-5 sm:pl-6 space-y-3">
              <p className="font-display text-xl sm:text-2xl font-bold leading-snug">
                The problem wasn&rsquo;t that an accident happened. Accidents happen.
              </p>
              <p className="text-[17px] font-semibold leading-relaxed text-[#1B4332]">
                The problem was that the business already knew the booked vehicle was unavailable,
                while the customer was still expecting the rental to proceed normally.
              </p>
            </div>

            <div className="grid sm:grid-cols-3 gap-3 my-7" aria-label="The disconnected rental records">
              {["The vehicle had become unavailable.", "The booking still existed.", "The customer was still expecting the rental."].map((line) => (
                <div key={line} className="rounded-xl bg-[#FAFAFA] border border-slate-200 p-4 text-sm font-medium leading-relaxed">
                  {line}
                </div>
              ))}
            </div>

            <div className="space-y-4 text-[17px] text-slate-700 leading-relaxed">
              <p className="font-semibold text-slate-900">
                Those pieces were not connected well enough for the change to reach the customer in time.
              </p>
              <p>
                This was not the first rental problem I had experienced, and it was one of several
                similar incidents I had heard about from other renters. Those stories were not proof
                of a market on their own, but they made the information-flow problem worth investigating.
              </p>
              <p>
                Together, those experiences made me look more closely at how
                rental businesses coordinate bookings, vehicles, staff and customers.
              </p>
              <p className="font-display text-xl font-bold text-slate-900 leading-snug">
                ConfirmPH grew from a simple question: what if the rental had one connected operational
                record, so when something changed with the vehicle, the affected booking and customer
                could be identified and acted on before pickup?
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// Real product component, framed and cropped. `aria-hidden` + pointer-events
// none is deliberate: this is presented as a screenshot-equivalent
// illustration, not an interactive widget (a real interactive tour lives at
// PRODUCT_TOUR_PATH, linked from the hero CTA and captioned under this
// visual for assistive tech).
function ProductVisual({ preloaded }) {
  return (
    <div
      aria-hidden="true"
      inert={true}
      className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden select-none"
      data-testid="investors-product-visual"
    >
      <div className="flex items-center gap-1.5 px-4 h-9 border-b border-slate-200 bg-slate-50">
        <span className="w-2.5 h-2.5 rounded-full bg-slate-300" />
        <span className="w-2.5 h-2.5 rounded-full bg-slate-300" />
        <span className="w-2.5 h-2.5 rounded-full bg-slate-300" />
        <span className="ml-3 text-[11px] font-mono text-slate-400">confirmph.app/portal</span>
      </div>
      <div className="relative h-[440px] overflow-hidden pointer-events-none">
        <div className="p-5 scale-[0.97] origin-top">
          <CustomerBookingDetail preloaded={preloaded} />
        </div>
        <div className="absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-white to-transparent" />
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------
// 5. Status strip. Reusable-architecture note: this renders straight off
// STATUS_STRIP_ITEMS (plain strings) today. Once there's something real to
// report, swap that array for objects like {label:"Active businesses",
// value:"12"} and give StatusMetric a value branch: nothing else on the
// page needs to change.
// -----------------------------------------------------------------------
function StatusStrip() {
  return (
    <section className="bg-white border-b border-slate-200" data-testid="investors-status-strip">
      <div className="max-w-[1180px] mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <ul className="flex flex-wrap items-center justify-center gap-x-8 gap-y-2 text-sm text-slate-600">
          {STATUS_STRIP_ITEMS.map((label, i) => (
            <li key={label} className="flex items-center gap-2">
              {i > 0 && <span className="hidden sm:inline text-slate-300" aria-hidden="true">·</span>}
              <span className="w-1.5 h-1.5 rounded-full bg-[#1B4332]" aria-hidden="true" />
              {label}
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

// -----------------------------------------------------------------------
// 6. THE PROBLEM
// -----------------------------------------------------------------------
const PROBLEM_STAGES = [
  { title: "Customer enquiry", where: "Messenger" },
  { title: "Booking", where: "Notebook / spreadsheet" },
  { title: "Availability", where: "Manual staff check" },
  { title: "Inspection", where: "Phone / separate records" },
  { title: "Follow-up", where: "Memory / separate reminders" },
];

function ProblemSection() {
  return (
    <section className="bg-white" data-testid="investors-problem">
      <div className="max-w-[1180px] mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-12">
        <SectionHeading eyebrow="The problem" title="One rental. Too many disconnected places." center />

        <div className="mt-12 flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-1">
          {PROBLEM_STAGES.map((s, i) => (
            <div key={s.title} className="flex flex-col sm:flex-row items-center gap-1">
              <div className="w-full sm:w-[150px] rounded-xl border border-slate-200 bg-[#FAFAFA] p-4 text-center">
                <div className="font-display font-semibold text-sm">{s.title}</div>
                <div className="mt-1 text-xs text-slate-500">{s.where}</div>
              </div>
              {i < PROBLEM_STAGES.length - 1 && <FlowArrow />}
            </div>
          ))}
        </div>

        <div className="mt-4 flex justify-center">
          <ArrowDown className="w-5 h-5 text-slate-300" aria-hidden="true" />
        </div>

        <div className="mt-4 max-w-md mx-auto rounded-xl bg-[#1B4332] text-white p-5 text-center">
          <div className="font-display font-bold text-lg">ConfirmPH</div>
          <div className="text-sm text-white/80 mt-0.5">One connected rental process.</div>
        </div>

        <div className="mt-10 max-w-[680px] mx-auto text-center">
          <p className="text-[17px] text-slate-600 leading-relaxed">
            When those records disagree, the result is double bookings, unavailable vehicles, missed
            follow-ups, staff confusion and more owner intervention.
          </p>
          <p className="mt-3 font-display text-xl font-semibold">
            The software problem is fragmentation. The business cost is lost control.
          </p>
        </div>
      </div>
    </section>
  );
}

// -----------------------------------------------------------------------
// 7. WHAT CONFIRMPH CONNECTS  (nav anchor: Product)
// -----------------------------------------------------------------------
const CONNECT_CARDS = [
  { icon: CalendarCheck2, title: "Bookings", desc: "Confirmations, agreement versions and acceptance, availability and rental records." },
  { icon: Car, title: "Fleet", desc: "Vehicles, legal-readiness evidence, inspections, damage and maintenance." },
  { icon: Users, title: "People", desc: "Customers, staff, company drivers and dispatch, and authorized self-drive drivers." },
  { icon: ClipboardList, title: "Operations", desc: "Handover, return, damage and dispute handling, rental closure, and Finance & Books." },
];

function ConnectSection() {
  return (
    <section id="product" className="bg-[#FAFAFA] scroll-mt-24" data-testid="investors-connect">
      <div className="max-w-[1180px] mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid lg:grid-cols-[0.8fr_1.2fr] gap-10 lg:gap-14 items-center">
          <div>
            <SectionHeading eyebrow="The product" title="One rental. One connected process." />
            <p className="mt-5 text-[17px] text-slate-600 leading-relaxed">
              The rental is the organizing unit, not the chat, vehicle, customer or spreadsheet.
              Bookings, fleet, people and operational records stay connected around the same work.
            </p>
            <p className="mt-5 font-display text-xl sm:text-2xl font-semibold leading-snug">
              The goal is not another dashboard. The goal is to make the rental easier to run.
            </p>
            <div className="mt-6">
              <Link to={PRODUCT_TOUR_PATH} className="inline-flex items-center gap-1 text-sm font-medium text-[#1B4332] hover:underline">
                Explore ConfirmPH <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            {CONNECT_CARDS.map((c, index) => (
              <div key={c.title} className={`rounded-xl border p-5 ${index === 0 ? "sm:col-span-2 bg-[#1B4332] border-[#1B4332] text-white" : "border-slate-200 bg-white"}`}>
                <div className={`w-9 h-9 rounded-lg grid place-items-center ${index === 0 ? "bg-white/15 text-white" : "bg-emerald-50 text-[#1B4332]"}`}>
                  <c.icon className="w-5 h-5" strokeWidth={1.8} />
                </div>
                <div className="mt-3 font-display font-semibold text-lg">{c.title}</div>
                <p className={`mt-1 text-sm leading-relaxed ${index === 0 ? "text-white/80" : "text-slate-600"}`}>{c.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

// -----------------------------------------------------------------------
// 8. WHY CONFIRMPH IS DIFFERENT
// -----------------------------------------------------------------------
function DifferentSection() {
  return (
    <section className="bg-white" data-testid="investors-different">
      <div className="max-w-[1180px] mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <SectionHeading eyebrow="Positioning" title="Isn't this just another booking system?" center />

        <div className="mt-12 grid sm:grid-cols-3 gap-5">
          <div className="rounded-xl border border-slate-200 bg-white p-6">
            <div className="font-display font-semibold text-lg">Booking software</div>
            <p className="mt-2 text-sm text-slate-600">Manages the reservation.</p>
          </div>
          <div className="rounded-xl border border-slate-200 bg-white p-6">
            <div className="font-display font-semibold text-lg">Fleet software</div>
            <p className="mt-2 text-sm text-slate-600">Manages the vehicle.</p>
          </div>
          <div className="rounded-xl border-2 border-[#1B4332] bg-emerald-50/40 p-6">
            <div className="font-display font-semibold text-lg text-[#1B4332]">ConfirmPH</div>
            <p className="mt-2 text-sm text-slate-700">Connects the rental around both.</p>
          </div>
        </div>

        <p className="mt-8 text-center text-[17px] text-slate-600 max-w-[680px] mx-auto leading-relaxed">
          Booking, vehicle, customer, staff, inspections, incidents, maintenance and follow-up remain
          connected to the same rental journey: from the rental agreement through handover and
          return to damage, claims or disputes where needed, and final closure. Business and fleet
          legal readiness, driver service and dispatch, and Finance &amp; Books sit on the same
          record.
        </p>
      </div>
    </section>
  );
}

// -----------------------------------------------------------------------
// 9. CURRENT STAGE
// -----------------------------------------------------------------------
// The corporation card is read from the canonical operator record (Task 2),
// never written here: it can only say a corporation exists once one is
// recorded and verified there.
function stageCards(contacts) {
  const corporation = contacts.legalEntityRecorded
    ? { label: "Philippine corporation", status: "Registered",
        desc: `ConfirmPH is operated by ${contacts.operatorName}.` }
    : { label: "Philippine corporation", status: "Not yet incorporated",
        desc: "The parent corporation is being prepared. Until it is registered, ConfirmPH is "
          + "operated as an independent Philippine software project." };
  return [
    { label: "Product", status: "Functional", desc: "The core product is functional and the remaining launch work is being completed." },
    corporation,
    { label: "Market", status: "Validation next", desc: "The immediate milestone is putting ConfirmPH into real rental operations." },
  ];
}

function StageSection() {
  const contacts = useOperatorContacts();
  const STAGE_CARDS = stageCards(contacts);
  return (
    <section className="bg-[#F4F4F5]" data-testid="investors-stage">
      <div className="max-w-[1180px] mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-16">
        <SectionHeading eyebrow="Where things stand" title="Current stage" center />

        <div className="mt-12 grid sm:grid-cols-3 gap-5">
          {STAGE_CARDS.map((c) => (
            <div key={c.label} className="rounded-xl border border-slate-200 bg-white p-6">
              <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">{c.label}</div>
              <div className="mt-2 font-display text-2xl font-bold text-[#1B4332]">{c.status}</div>
              <p className="mt-3 text-sm text-slate-600 leading-relaxed">{c.desc}</p>
            </div>
          ))}
        </div>

        <div className="mt-12 max-w-[680px] mx-auto text-center">
          <p className="font-display text-2xl sm:text-3xl font-bold">The next major validation milestone is commercial.</p>
          <p className="mt-4 text-[17px] text-slate-600 leading-relaxed">
            We need to prove that businesses adopt ConfirmPH, return to it and eventually pay for it
            consistently.
          </p>
          <p className="mt-2 text-[17px] text-slate-900 font-medium">
            We will measure what customers actually do.
          </p>
        </div>
        <InitialCustomerPanel />
      </div>
    </section>
  );
}

function InitialCustomerPanel() {
  return (
    <div className="mt-12 rounded-2xl border border-slate-200 bg-white overflow-hidden grid lg:grid-cols-[0.7fr_1.3fr]" data-testid="investors-initial-customer">
      <div className="bg-[#1B4332] text-white p-6 sm:p-7">
        <div className="text-xs uppercase tracking-[0.2em] text-emerald-200 font-semibold">Initial customer</div>
        <h2 className="mt-3 font-display text-2xl font-bold">Start where coordination already hurts.</h2>
      </div>
      <div className="p-6 sm:p-7">
        <p className="text-[16px] text-slate-700 leading-relaxed">
          ConfirmPH is initially built for independent Philippine vehicle-rental operators where
          an owner or manager still coordinates bookings, vehicles, customers and staff through
          messaging, spreadsheets, paper or memory.
        </p>
        <p className="mt-3 text-[16px] font-medium leading-relaxed">
          The first wedge is a business large enough to feel coordination pain, but still
          underserved by complex enterprise rental software.
        </p>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------
// 10. FIRST MARKET TEST  (nav anchor: Market Test)
// -----------------------------------------------------------------------
const MARKET_TEST_STAGES = [
  "Owner / Manager", "Demo", "First real rental", "Second real rental", "Continued use", "Paid business",
];

function MarketTestSection() {
  return (
    <section id="market-test" className="bg-white scroll-mt-24" data-testid="investors-market-test">
      <div className="max-w-[1180px] mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <SectionHeading eyebrow="Go-to-market" title="Our first market test" center />
        <p className="mt-4 text-[17px] text-slate-600 max-w-[680px] mx-auto text-center leading-relaxed">
          We are starting hands-on. Representatives will meet rental owners and managers, understand
          how they currently operate, demonstrate those same situations inside ConfirmPH and help
          them get started.
        </p>

        <div className="mt-12 flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-1 flex-wrap">
          {MARKET_TEST_STAGES.map((label, i) => {
            const emphasized = label === "Second real rental";
            return (
              <div key={label} className="flex flex-col sm:flex-row items-center gap-1">
                <div
                  className={
                    emphasized
                      ? "w-full sm:w-[160px] rounded-xl border-2 border-[#1B4332] bg-emerald-50/50 p-4 text-center shadow-sm"
                      : "w-full sm:w-[140px] rounded-xl border border-slate-200 bg-[#FAFAFA] p-4 text-center"
                  }
                  data-testid={emphasized ? "investors-second-rental-node" : undefined}
                >
                  <div className={emphasized ? "font-display font-bold text-sm text-[#1B4332]" : "font-display font-semibold text-sm"}>
                    {label}
                  </div>
                </div>
                {i < MARKET_TEST_STAGES.length - 1 && <FlowArrow />}
              </div>
            );
          })}
        </div>

        <div className="mt-14 max-w-[680px] mx-auto rounded-2xl border border-slate-200 bg-[#FAFAFA] p-7 sm:p-8 text-center">
          <p className="font-display text-xl sm:text-3xl font-bold">Registration isn&rsquo;t adoption.</p>
          <p className="mt-3 text-[17px] text-slate-600 leading-relaxed">
            A signup can happen while our representative is standing there.
            <br />
            <strong className="text-[#1B4332]">A second real rental means the business chose to come back.</strong>
          </p>
        </div>
      </div>
    </section>
  );
}

// -----------------------------------------------------------------------
// 11. WHAT WE INTEND TO PROVE
// -----------------------------------------------------------------------
const PROVE_CARDS = [
  { title: "Demo acceptance", q: "Of qualified businesses approached, how many agree to see the product?" },
  { title: "First-rental activation", q: "How many businesses process a genuine customer rental?" },
  { title: "Second-rental rate", q: "How many activated businesses return and process another real rental?", emphasis: true },
  { title: "Retention", q: "How many continue using ConfirmPH over the measurement period?" },
  { title: "Paid conversion", q: "How many decide the operational value is worth paying for?" },
  { title: "Representative productivity", q: "Can another trained representative reproduce the same adoption pattern?" },
];

function ProveSection() {
  return (
    <section className="bg-[#FAFAFA]" data-testid="investors-prove">
      <div className="max-w-[1180px] mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <SectionHeading eyebrow="Measurement" title="What we intend to prove" center />

        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {PROVE_CARDS.map((c) => (
            <div key={c.title} className={c.emphasis ? "rounded-xl border-2 border-[#1B4332] bg-emerald-50/50 p-5" : "rounded-xl border border-slate-200 bg-white p-5"}>
              <div className={c.emphasis ? "font-display font-bold text-base text-[#1B4332]" : "font-display font-semibold text-base"}>{c.title}</div>
              <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">{c.q}</p>
            </div>
          ))}
        </div>

        <div className="mt-10 max-w-[680px] mx-auto text-center space-y-2">
          <p className="text-[17px] text-slate-900 font-medium">
            We will scale acquisition only when the customer behavior justifies it.
          </p>
          <p className="text-[17px] text-slate-600">
            If the model works, capital can accelerate it. If something does not work, we want to
            know early.
          </p>
        </div>
      </div>
    </section>
  );
}

// -----------------------------------------------------------------------
// 12. BUSINESS MODEL  (nav anchor: Business Model)
// -----------------------------------------------------------------------
const MODEL_STAGES = [
  { label: "START FREE", desc: "Low-friction entry for small operators" },
  { label: "RUN REAL RENTALS", desc: "ConfirmPH enters everyday operations" },
  { label: "DEPEND ON THE RECORD", desc: "More bookings, people and vehicles stay connected" },
  { label: "NEEDS EXPAND", desc: "Capacity, staff access and advanced workflows matter more" },
  { label: "PAID PLAN", desc: "Recurring revenue follows sustained operational value" },
];

function BusinessModelSection() {
  return (
    <section id="business-model" className="bg-white scroll-mt-24" data-testid="investors-business-model">
      <div className="max-w-[1180px] mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <SectionHeading eyebrow="Planned business model" title="Start free. Become useful. Grow with the business." center />

        <div className="mt-12 flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-1">
          {MODEL_STAGES.map((s, i) => (
            <div key={s.label} className="flex flex-col sm:flex-row items-center gap-1">
              <div className="w-full sm:w-[160px] rounded-xl border border-slate-200 bg-[#FAFAFA] p-4 text-center">
                <div className="font-display font-bold text-sm tracking-wide text-[#1B4332]">{s.label}</div>
                <div className="mt-1 text-xs text-slate-500 leading-snug">{s.desc}</div>
              </div>
              {i < MODEL_STAGES.length - 1 && <FlowArrow />}
            </div>
          ))}
        </div>
        <p className="mt-8 text-center text-[17px] font-medium max-w-[680px] mx-auto">
          Revenue is designed to grow with operational depth, not merely account creation.
        </p>
        <p className="mt-4 text-center text-sm text-slate-600 max-w-[680px] mx-auto leading-relaxed"
           data-testid="investors-money-separation">
          ConfirmPH earns from its own subscriptions, add-ons and clearly labelled Sponsored
          placement. Rental payments, advances and security deposits stay between renters and
          rental businesses; ConfirmPH records them but does not hold them. Paying ConfirmPH never
          buys a verification, legal-readiness or safety status.
        </p>
      </div>
    </section>
  );
}

// -----------------------------------------------------------------------
// 13. WHY START WITH RENTALS
// -----------------------------------------------------------------------
function WhyRentalsSection() {
  return (
    <section className="bg-[#10241B] text-white" data-testid="investors-why-rentals">
      <div className="max-w-[1180px] mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-14 grid md:grid-cols-2 gap-10 md:gap-14">
        <div>
          <SectionHeading eyebrow="Why rentals" title="Why start with rental businesses?" compact light />
          <p className="mt-5 text-[17px] text-white/75 leading-relaxed">
            A rental depends on customers, vehicles, staff and timing staying coordinated.
          </p>
          <p className="mt-3 text-[17px] text-white/75 leading-relaxed">
            When information breaks between those pieces, the failure becomes visible quickly: an
            unavailable vehicle, a missed handover, an unresolved issue or an unhappy customer.
          </p>
          <p className="mt-5 text-[17px] text-white font-medium leading-relaxed">
            ConfirmPH starts where operational fragmentation is frequent, visible and expensive.
          </p>
        </div>
        <div className="md:border-l md:border-white/15 md:pl-14" data-testid="investors-why-now">
          <SectionHeading eyebrow="Why now" title="Customer contact became digital. Operations often did not." compact light />
          <p className="text-[17px] text-white/80 leading-relaxed">
            Messaging and social channels made it easier for customers to reach rental businesses.
            They did not automatically connect the booking, vehicle, handover, staff work and follow-up behind the conversation.
          </p>
          <p className="mt-4 text-[17px] font-medium leading-relaxed">
            ConfirmPH is built for the gap between digital customer demand and manually coordinated rental operations.
          </p>
        </div>
      </div>
    </section>
  );
}

const COMPOUND_ITEMS = [
  { title: "Workflow habit", desc: "Owners and staff increasingly know where to look when a rental changes." },
  { title: "Operational history", desc: "Bookings, vehicles, inspections and incidents become more useful when they remain connected." },
  { title: "Reputation history", desc: "Customer confirmations and booking-linked reviews can remain tied to actual rental activity." },
  { title: "Distribution learning", desc: "Each market test can improve how ConfirmPH finds, activates and supports rental businesses." },
];

function CompoundSection() {
  return (
    <section className="bg-[#FAFAFA]" data-testid="investors-compound">
      <div className="max-w-[1180px] mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid lg:grid-cols-[0.75fr_1.25fr] gap-10 lg:gap-14">
          <div>
            <SectionHeading eyebrow="Potential defensibility" title="What could compound if adoption works" />
            <p className="mt-5 text-[17px] text-slate-600 leading-relaxed">
              The software alone is not the entire advantage. Value can deepen as real workflows,
              connected records and local distribution learning accumulate.
            </p>
            <p className="mt-5 text-sm font-semibold text-[#1B4332]">
              None of these are a moat today. They are mechanisms that may compound only if adoption is proven.
            </p>
          </div>
          <div className="divide-y divide-slate-200 border-y border-slate-200">
            {COMPOUND_ITEMS.map((item, index) => (
              <div key={item.title} className="grid grid-cols-[42px_1fr] gap-4 py-5">
                <span className="font-mono text-sm text-slate-400">0{index + 1}</span>
                <div>
                  <h3 className="font-display text-lg font-semibold">{item.title}</h3>
                  <p className="mt-1 text-sm text-slate-600 leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

// -----------------------------------------------------------------------
// 14. LONG-TERM OPPORTUNITY  (nav anchor: Vision)
// -----------------------------------------------------------------------
const OPPORTUNITY_STAGES = [
  { n: "01", title: "Operations", desc: "Rental businesses run more of the rental through ConfirmPH." },
  { n: "02", title: "Reputation", desc: "Completed rentals support connected customer history, business records and booking-linked reviews." },
  { n: "03", title: "Discovery", desc: "That reputation information can help customers evaluate rental businesses and begin the next rental." },
];

function OpportunitySection() {
  return (
    <section id="vision" className="bg-white scroll-mt-24" data-testid="investors-opportunity">
      <div className="max-w-[1180px] mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <SectionHeading eyebrow="Vision" title="Operations create the foundation for reputation and discovery." center />

        <div className="relative mt-12 flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-1">
          <div className="hidden sm:block absolute left-[18%] right-[18%] top-1/2 h-px bg-emerald-200" aria-hidden="true" />
          {OPPORTUNITY_STAGES.map((s, i) => (
            <div key={s.n} className="relative z-10 flex flex-col sm:flex-row items-center gap-1">
              <div className={i === 1 ? "w-full sm:w-[240px] rounded-xl border-2 border-[#1B4332] bg-emerald-50 p-5" : "w-full sm:w-[240px] rounded-xl border border-slate-200 bg-white p-5"}>
                <div className="text-xs font-mono text-slate-400">{s.n}</div>
                <div className="mt-1 font-display font-semibold text-base uppercase tracking-wide">{s.title}</div>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{s.desc}</p>
              </div>
              {i < OPPORTUNITY_STAGES.length - 1 && <FlowArrow />}
            </div>
          ))}
        </div>

        <div className="mt-10 max-w-[680px] mx-auto text-center">
          <p className="text-[17px] text-slate-600 leading-relaxed">
            The operating record creates the foundation. Reputation becomes more useful because it
            is connected to real rental activity. Better discovery can send demand back into the operation.
          </p>
          <p className="mt-3 text-sm font-medium text-[#1B4332]">That opportunity must be earned through adoption.</p>
        </div>
      </div>
    </section>
  );
}

// -----------------------------------------------------------------------
// 16. FOUNDER SECTION
// -----------------------------------------------------------------------
function FounderSection() {
  return (
    <section className="bg-white" data-testid="investors-founder">
      <div className="max-w-[1180px] mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="max-w-[680px] mx-auto text-center">
          <SectionHeading eyebrow="Founder" title="Built from the problem, then tested beyond it." center compact />

          <div className="mt-8 flex flex-col items-center">
            {/* No real photo exists yet: an initials avatar keeps this
                honest (nothing fabricated) while leaving a real <img/> a
                one-line swap once a photo is available. */}
            <div
              aria-hidden="true"
              className="w-16 h-16 rounded-full bg-[#1B4332] text-white grid place-items-center font-display font-semibold text-xl"
            >
              JL
            </div>
            <div className="mt-3 font-display text-lg font-semibold">{FOUNDER_NAME}</div>
            <div className="text-sm text-slate-500">Founder, ConfirmPH</div>
          </div>

          <p className="mt-6 text-[17px] text-slate-600 leading-relaxed">
            My own rental experience was the starting point, not the conclusion.
          </p>
          <p className="mt-3 text-[17px] text-slate-600 leading-relaxed">
            ConfirmPH is being developed around a simple principle: understand how rental businesses
            actually operate, build around those workflows, and then test whether the product solves
            a recurring problem for real businesses.
          </p>
          <p className="mt-4 text-[17px] text-slate-900 font-semibold">
            One experience can reveal a problem. The market has to prove whether the solution matters.
          </p>
        </div>
      </div>
    </section>
  );
}

// -----------------------------------------------------------------------
// 17. FINAL INVESTOR CTA
// -----------------------------------------------------------------------
const EXPERTISE_LABELS = [
  "Philippine SMB distribution", "Field sales", "Vertical software", "Rental operations", "Early commercial teams",
];

function FinalCtaSection() {
  return (
    <section id="contact" className="bg-[#10241B] text-white scroll-mt-24" data-testid="investors-final-cta">
      <div className="max-w-[1180px] mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-12">
        <div className="max-w-[680px] mx-auto text-center">
          <h2 className="font-display font-bold text-[1.9rem] sm:text-[2.5rem] leading-tight">
            The next milestone is repeatable commercial adoption.
          </h2>
          <p className="mt-4 text-[17px] leading-relaxed text-white/80">
            The core product is functional. The immediate commercial test is whether rental businesses
            adopt it, return for another real rental, continue using it and eventually pay for it.
          </p>
          <p className="mt-3 text-[17px] leading-relaxed text-white/80">
            We are speaking with a small number of investors and operators who understand early-stage
            B2B distribution in the Philippines and can help validate that motion responsibly.
          </p>

          <ul className="mt-6 flex flex-wrap justify-center gap-2">
            {EXPERTISE_LABELS.map((l) => (
              <li key={l} className="text-xs px-3 py-1.5 rounded-full border border-white/25 text-white/80">
                {l}
              </li>
            ))}
          </ul>

          <p className="mt-6 text-[17px] leading-relaxed text-white font-medium">
            The immediate challenge is not adding hype. It is proving a repeatable path from rental
            owner to real usage, retention and a paid account.
          </p>

          <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
            <a
              href={`mailto:${CONTACT_EMAIL}`}
              data-testid="investors-final-cta-talk"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-white text-[#10241B] font-semibold text-sm sm:text-base hover:bg-white/90"
            >
              <Mail className="w-4 h-4" /> Talk to Jeanjen
            </a>
            <Link
              to="/"
              data-testid="investors-final-cta-view"
              className="inline-flex items-center px-6 py-3 rounded-lg border border-white/30 text-white font-semibold text-sm sm:text-base hover:bg-white/10"
            >
              View ConfirmPH
            </Link>
          </div>

          <div className="mt-7 pt-6 border-t border-white/15 text-sm text-white/70 space-y-0.5">
            <div className="font-display text-white font-semibold">{FOUNDER_NAME}</div>
            <div>ConfirmPH</div>
            <div>confirm.ph</div>
            <div>
              <a href={`mailto:${CONTACT_EMAIL}`} className="underline hover:text-white">{CONTACT_EMAIL}</a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// -----------------------------------------------------------------------
// Minimal footer: intentionally not the full 4-column PublicFooter. Keeps
// the page isolated and short; the legal minimum plus a way back to the
// main site is enough here.
// -----------------------------------------------------------------------
function InvestorFooter() {
  const year = new Date().getFullYear();
  return (
    <footer className="bg-[#10241B] text-white/60 border-t border-white/10" data-testid="investors-footer">
      <div className="max-w-[1180px] mx-auto px-4 sm:px-6 lg:px-8 py-7 text-xs">
        <p className="max-w-[900px] text-white/55 leading-relaxed">
          ConfirmPH is currently pre-launch and has not yet established commercial adoption,
          repeatable customer acquisition or sustained paid conversion. Statements about plans,
          rollout, growth and future opportunities describe current intentions and may change.
          This page is provided for general information only and does not constitute an offer to
          sell or a solicitation of an offer to purchase securities. Any securities review, share
          issuance, stock and transfer book matters and foreign-investment analysis are legal work
          still to be done with qualified counsel; nothing in the ConfirmPH software completes them.
        </p>
        <div className="mt-5 pt-5 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-3">
          <div>&copy; {year} ConfirmPH</div>
          <div className="flex items-center gap-4">
            <Link to="/" className="hover:text-white">confirm.ph</Link>
            <Link to="/privacy" className="hover:text-white">Privacy</Link>
            <Link to="/terms" className="hover:text-white">Terms</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}

// -----------------------------------------------------------------------
// Mobile sticky CTA: small, single action, not intrusive.
// -----------------------------------------------------------------------
function MobileStickyCta() {
  return (
    <div className="sm:hidden fixed bottom-0 inset-x-0 z-30 p-3 bg-white/90 backdrop-blur-xl border-t border-slate-200">
      <a
        href="#contact"
        data-testid="investors-mobile-sticky-cta"
        className="flex items-center justify-center gap-2 w-full py-3 rounded-lg btn-brand font-semibold text-sm"
      >
        Talk to Jeanjen
      </a>
    </div>
  );
}

// -----------------------------------------------------------------------
// Shared small pieces
// -----------------------------------------------------------------------
function SectionHeading({ eyebrow, title, center = false, compact = false, light = false }) {
  return (
    <div className={center ? "text-center" : ""}>
      <div className={`text-xs uppercase tracking-[0.2em] font-semibold ${light ? "text-emerald-200" : "text-slate-500"}`}>{eyebrow}</div>
      <h2 className={`mt-2 font-display font-bold leading-tight ${compact ? "text-[1.75rem] sm:text-[2rem]" : "text-[1.875rem] sm:text-[2.25rem] lg:text-[2.5rem]"} ${center ? "max-w-[720px] mx-auto" : ""}`}>
        {title}
      </h2>
    </div>
  );
}

function FlowArrow() {
  return (
    <div className="flex items-center justify-center text-slate-300 shrink-0 px-1 py-1 sm:px-2" aria-hidden="true">
      <ArrowRight className="hidden sm:block w-5 h-5" />
      <ArrowDown className="sm:hidden w-5 h-5" />
    </div>
  );
}
